import { useQuery } from 'react-query';
import { FeaturedReview } from 'rwl-shared/dist/models';
import { useAPIRoute, useV2APIRoute } from '../../hooks/useAPIRoute';
import { useGlobal } from '../../hooks';
import { useSelectedPostCode } from '../../hooks/useSelectedPostCode';
import { fetchV2FeaturedReviewById } from '../v2/fetchV2FeaturedContent';

type UseFetchReviewParams = {
    reviewId?: string;
    enabled: boolean;
};

export const useFetchReview = ({ reviewId, enabled }: UseFetchReviewParams) => {
    const { accountId } = useGlobal();
    const url = useAPIRoute('serviceareas/featured/review', 'v1');
    const v2ServiceAreaContentUrl = useV2APIRoute('serviceArea/content');
    // Selected area, needed to page the v2 content — v2 has no global by-id
    // lookup. Read from the lifted context because this hook runs above
    // PlacesAutocompleteProvider (see useSelectedPostCode).
    const postCode = useSelectedPostCode();

    return useQuery<FeaturedReview>(
        // v2 is part of the key so flipping the flag cannot serve a response the
        // other backend produced. postCode belongs in the key only when v2 is
        // active (it resolves the review within the selected area) — folding it
        // in for v1 would invalidate an already-cached by-id result every time
        // the visitor picks a new place, flashing a needless reload of the pop-out.
        [
            'useFetchReview',
            accountId,
            reviewId,
            v2ServiceAreaContentUrl ? postCode : undefined,
            v2ServiceAreaContentUrl ? 'v2' : 'v1',
        ],
        async () => {
            if (!reviewId || !accountId) {
                return undefined;
            }

            if (v2ServiceAreaContentUrl) {
                const v2Review = await fetchV2FeaturedReviewById({
                    serviceAreaContentUrl: v2ServiceAreaContentUrl,
                    accountId,
                    postCode,
                    reviewId,
                });
                // Undefined means v2 could not resolve it (no selected area, the
                // review is past the stored cap, or a page failed) — fall back to
                // the v1 by-id endpoint.
                if (v2Review) {
                    return v2Review;
                }
            }

            try {
                const result = await fetch(
                    `${url}?` +
                        new URLSearchParams({
                            accountId: accountId,
                            reviewId: reviewId,
                        }),
                    {
                        method: 'GET',
                    }
                );

                return result.json();
            } catch (e) {
                console.error('useFetchReview error:', e);
                throw e;
            }
        },
        {
            enabled: !!reviewId && !!accountId && enabled,
            staleTime: 1000 * 60 * 60 * 24, // 24 hours
            cacheTime: 1000 * 10,
        }
    );
};
